import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  assignConversation,
  buildChatwootConversationUrl,
  deleteConversation,
  fetchChatwootMeta,
  setConversationLabels,
  toggleConversationStatus,
  updateConversationPriority
} from '../api/chatwoot';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import { useAuth } from '../context/AuthContext';
import {
  canAssignChatwootAgent,
  canDeleteChatwootConversation,
  getAgentDisplayInitials,
  CHATWOOT_DELETE_CONVERSATION_PERMISSION
} from '../utils/chatwootPermissions';
import ChatwootAgentCheckIcon from './ChatwootAgentCheckIcon';
import { getFloatingPanelFixedStyle } from '../utils/floatingPanelPosition';

const CONV_MENU_WIDTH_PX = 260;

export { CHATWOOT_DELETE_CONVERSATION_PERMISSION, canDeleteChatwootConversation };

const PRIORITY_OPTIONS = [
  { value: 'none', label: 'Sin prioridad' },
  { value: 'low', label: 'Baja' },
  { value: 'medium', label: 'Media' },
  { value: 'high', label: 'Alta' },
  { value: 'urgent', label: 'Urgente' }
];

const ACTION_OVERLAY_HINT = 'Actualizando la conversación en Chatwoot. Esperá un momento.';

function buildActionOverlay(title, ariaLabel = title) {
  return {
    title,
    hint: ACTION_OVERLAY_HINT,
    ariaLabel
  };
}

/** Oculto en CRM: la gestión de etiquetas y apertura en Chatwoot se hace fuera del menú. */
const SHOW_CONV_MENU_LABELS = false;
const SHOW_CONV_MENU_OPEN_IN_TAB = false;

export default function ChatwootConversationMenu({
  conversation,
  accountId,
  busy = false,
  onBusyChange,
  onPatch,
  onDeleted,
  onNotify,
  showProfilePanel = false,
  onToggleProfilePanel,
  showProfileToggle = false,
  allowAssignAgent = null,
  prefetchedAgents = null,
  conversationDisplayName = ''
}) {
  const { user } = useAuth();
  const showDeleteConversation = canDeleteChatwootConversation(user);
  const showAssignAgent =
    allowAssignAgent === null ? canAssignChatwootAgent(user) : Boolean(allowAssignAgent);
  const [open, setOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null);
  const [meta, setMeta] = useState({ agents: [], labels: [], agents_source: null });
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaLoadError, setMetaLoadError] = useState(false);
  const [deletingConversation, setDeletingConversation] = useState(false);
  const [actionOverlay, setActionOverlay] = useState(null);
  const wrapRef = useRef(null);
  const menuPanelRef = useRef(null);
  const [menuPanelStyle, setMenuPanelStyle] = useState(null);
  const onNotifyRef = useRef(onNotify);
  const metaFetchInFlightRef = useRef(false);
  const metaLoadedRef = useRef(false);

  useEffect(() => {
    onNotifyRef.current = onNotify;
  }, [onNotify]);

  useEffect(() => {
    if (!deletingConversation && !actionOverlay) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [deletingConversation, actionOverlay]);

  const applyPrefetchedAgents = useCallback((agents) => {
    const list = Array.isArray(agents) ? agents.filter((a) => Number(a?.id) > 0) : [];
    if (!list.length) return false;
    setMeta((prev) => ({
      ...prev,
      agents: list,
      agents_source: prev.agents_source || 'crm'
    }));
    metaLoadedRef.current = true;
    setMetaLoadError(false);
    return true;
  }, []);

  useEffect(() => {
    if (!prefetchedAgents?.length) return;
    applyPrefetchedAgents(prefetchedAgents);
  }, [prefetchedAgents, applyPrefetchedAgents]);

  const loadMeta = useCallback(async ({ force = false } = {}) => {
    if (metaFetchInFlightRef.current) return;
    if (!force && metaLoadedRef.current) return;

    metaFetchInFlightRef.current = true;
    setMetaLoading(true);
    setMetaLoadError(false);

    try {
      const json = await fetchChatwootMeta();
      const data = json?.data || { agents: [], labels: [], agents_source: null };
      setMeta(data);
      metaLoadedRef.current = true;
    } catch {
      setMetaLoadError(true);
      metaLoadedRef.current = true;
      onNotifyRef.current?.('No se pudieron cargar agentes o etiquetas', 'error');
    } finally {
      metaFetchInFlightRef.current = false;
      setMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    if (metaLoadedRef.current) return;
    if (applyPrefetchedAgents(prefetchedAgents)) return;
    void loadMeta();
  }, [open, prefetchedAgents, applyPrefetchedAgents, loadMeta]);

  useEffect(() => {
    if (!open || submenu !== 'agent') return;
    if (metaLoadedRef.current || metaLoading) return;
    if (applyPrefetchedAgents(prefetchedAgents)) return;
    void loadMeta();
  }, [open, submenu, metaLoading, prefetchedAgents, applyPrefetchedAgents, loadMeta]);

  useEffect(() => {
    if (!open) {
      setMenuPanelStyle(null);
      return undefined;
    }

    function updateMenuPosition() {
      const anchor = wrapRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const estimatedHeight = menuPanelRef.current?.offsetHeight || (submenu ? 420 : 360);
      setMenuPanelStyle(
        getFloatingPanelFixedStyle(rect, {
          width: CONV_MENU_WIDTH_PX,
          estimatedHeight,
          gap: 6,
          alignRight: true,
          zIndex: 12050
        })
      );
    }

    updateMenuPosition();
    const raf = requestAnimationFrame(updateMenuPosition);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, submenu, metaLoading, meta.agents.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const target = e.target;
      if (wrapRef.current?.contains(target)) return;
      if (menuPanelRef.current?.contains(target)) return;
      setOpen(false);
      setSubmenu(null);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const conversationLabels = useMemo(
    () => new Set((conversation?.labels || []).map((l) => String(l).toLowerCase())),
    [conversation?.labels]
  );

  async function runAction(fn, successMsg, overlay) {
    if (!conversation?.id || busy || actionOverlay) return;
    if (overlay) {
      setActionOverlay(overlay);
      setOpen(false);
      setSubmenu(null);
    }
    onBusyChange?.(true);
    try {
      const result = await fn();
      if (result?.data) onPatch?.(result.data);
      onNotify?.(successMsg, 'ok');
      setOpen(false);
      setSubmenu(null);
    } catch (err) {
      onNotify?.(err.message || 'No se pudo completar la acción', 'error');
    } finally {
      onBusyChange?.(false);
      setActionOverlay(null);
    }
  }

  const menuBtnDisabled = !conversation?.id || busy || deletingConversation || Boolean(actionOverlay);

  const blockingOverlayConfig = deletingConversation
    ? {
        title: 'Eliminando conversación',
        hint: 'Esperá un momento. No cierres esta pestaña.',
        ariaLabel: 'Eliminando conversación'
      }
    : actionOverlay;

  const blockingOverlay =
    blockingOverlayConfig &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className="crm-cw-delete-overlay"
        role="alertdialog"
        aria-modal="true"
        aria-busy="true"
        aria-live="assertive"
        aria-label={blockingOverlayConfig.ariaLabel || blockingOverlayConfig.title}
      >
        <div className="crm-cw-delete-overlay__card">
          <span className="crm-cw-pane-spinner crm-cw-delete-overlay__spinner" aria-hidden="true" />
          <strong className="crm-cw-delete-overlay__title">{blockingOverlayConfig.title}</strong>
          {blockingOverlayConfig.hint ? (
            <p className="crm-cw-delete-overlay__hint">{blockingOverlayConfig.hint}</p>
          ) : null}
        </div>
      </div>,
      document.body
    );

  return (
    <div className="crm-cw-conv-menu-wrap" ref={wrapRef}>
      {blockingOverlay}
      <button
        type="button"
        className={`crm-cw-conv-menu-btn ${open ? 'active' : ''}`}
        title="Acciones de conversación"
        aria-label="Acciones de conversación"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={menuBtnDisabled}
        onClick={() => {
          const willOpen = !open;
          setOpen(willOpen);
          setSubmenu(null);
          if (willOpen && wrapRef.current) {
            const rect = wrapRef.current.getBoundingClientRect();
            setMenuPanelStyle(
              getFloatingPanelFixedStyle(rect, {
                width: CONV_MENU_WIDTH_PX,
                estimatedHeight: submenu ? 420 : 360,
                gap: 6,
                alignRight: true,
                zIndex: 12050
              })
            );
          } else if (!willOpen) {
            setMenuPanelStyle(null);
          }
        }}
      >
        ⋯
      </button>

      {open && menuPanelStyle && typeof document !== 'undefined'
        ? createPortal(
        <div
          ref={menuPanelRef}
          className="crm-cw-conv-menu crm-cw-conv-menu--portal"
          role="menu"
          style={menuPanelStyle}
        >
          {showProfileToggle ? (
            <>
              <button
                type="button"
                className="crm-cw-conv-menu__item"
                role="menuitem"
                onClick={() => {
                  onToggleProfilePanel?.();
                  setOpen(false);
                  setSubmenu(null);
                }}
              >
                <span className="crm-cw-conv-menu__icon" aria-hidden="true">
                  {showProfilePanel ? '◧' : '◨'}
                </span>
                {showProfilePanel ? 'Ocultar ficha del cliente' : 'Mostrar ficha del cliente'}
              </button>
              <div className="crm-cw-conv-menu__sep" role="separator" />
            </>
          ) : null}
          <button
            type="button"
            className="crm-cw-conv-menu__item crm-cw-conv-menu__item--unavailable"
            role="menuitem"
            disabled
            title="Llamadas no disponibles por ahora"
            aria-disabled="true"
          >
            <span className="crm-cw-conv-menu__icon" aria-hidden="true">📞</span>
            Llamar
          </button>
          <div className="crm-cw-conv-menu__sep" role="separator" />
          <button
            type="button"
            className="crm-cw-conv-menu__item crm-cw-conv-menu__item--primary"
            role="menuitem"
            onClick={() =>
              runAction(
                () => toggleConversationStatus(conversation.id, { status: 'resolved' }),
                'Conversación marcada como resuelta',
                buildActionOverlay('Marcando como resuelta', 'Marcando conversación como resuelta')
              )
            }
          >
            <span className="crm-cw-conv-menu__icon" aria-hidden="true">✓</span>
            Marcar como resuelto
          </button>
          <button
            type="button"
            className="crm-cw-conv-menu__item"
            role="menuitem"
            onClick={() =>
              runAction(
                () => toggleConversationStatus(conversation.id, { status: 'pending' }),
                'Conversación marcada como pendiente',
                buildActionOverlay('Marcando como pendiente', 'Marcando conversación como pendiente')
              )
            }
          >
            <span className="crm-cw-conv-menu__icon" aria-hidden="true">🕐</span>
            Marcar como pendiente
          </button>
          <button
            type="button"
            className="crm-cw-conv-menu__item"
            role="menuitem"
            onClick={() => setSubmenu(submenu === 'priority' ? null : 'priority')}
          >
            <span className="crm-cw-conv-menu__icon" aria-hidden="true">⚠</span>
            Prioridad
            <span className="crm-cw-conv-menu__chevron" aria-hidden="true">›</span>
          </button>
          {submenu === 'priority' ? (
            <div className="crm-cw-conv-submenu" role="group">
              {PRIORITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`crm-cw-conv-menu__item ${
                    conversation?.priority === opt.value ? 'is-selected' : ''
                  }`}
                  onClick={() =>
                    runAction(
                      () => updateConversationPriority(conversation.id, opt.value),
                      `Prioridad: ${opt.label}`,
                      buildActionOverlay(
                        `Prioridad: ${opt.label}`,
                        `Actualizando prioridad a ${opt.label}`
                      )
                    )
                  }
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : null}

          {SHOW_CONV_MENU_LABELS ? (
            <>
              <button
                type="button"
                className="crm-cw-conv-menu__item"
                role="menuitem"
                onClick={() => setSubmenu(submenu === 'labels' ? null : 'labels')}
              >
                <span className="crm-cw-conv-menu__icon" aria-hidden="true">🏷</span>
                Asignar etiqueta
                <span className="crm-cw-conv-menu__chevron" aria-hidden="true">›</span>
              </button>
              {submenu === 'labels' ? (
                <div className="crm-cw-conv-submenu crm-cw-conv-submenu--scroll" role="group">
                  {metaLoading ? (
                    <div className="crm-cw-conv-menu__hint">Cargando etiquetas…</div>
                  ) : meta.labels.length === 0 ? (
                    <div className="crm-cw-conv-menu__hint">Sin etiquetas en la cuenta</div>
                  ) : (
                    meta.labels.map((label) => {
                      const active = conversationLabels.has(String(label.title).toLowerCase());
                      return (
                        <button
                          key={label.id || label.title}
                          type="button"
                          className={`crm-cw-conv-menu__item ${active ? 'is-selected' : ''}`}
                          onClick={() => {
                            const next = active
                              ? (conversation.labels || []).filter(
                                  (l) => String(l).toLowerCase() !== String(label.title).toLowerCase()
                                )
                              : [...(conversation.labels || []), label.title];
                            runAction(
                              () => setConversationLabels(conversation.id, next),
                              active ? 'Etiqueta quitada' : 'Etiqueta asignada',
                              buildActionOverlay(
                                active ? 'Quitando etiqueta' : 'Asignando etiqueta',
                                active ? 'Quitando etiqueta de la conversación' : 'Asignando etiqueta a la conversación'
                              )
                            );
                          }}
                        >
                          {label.title}
                        </button>
                      );
                    })
                  )}
                </div>
              ) : null}
            </>
          ) : null}

          {showAssignAgent ? (
            <>
              <button
                type="button"
                className="crm-cw-conv-menu__item"
                role="menuitem"
                onClick={() => setSubmenu(submenu === 'agent' ? null : 'agent')}
              >
                <span className="crm-cw-conv-menu__icon" aria-hidden="true">👤</span>
                Asignar operador
                <span className="crm-cw-conv-menu__chevron" aria-hidden="true">›</span>
              </button>
              {submenu === 'agent' ? (
                <div
                  className="crm-cw-conv-submenu crm-cw-conv-submenu--agents crm-cw-conv-submenu--scroll"
                  role="group"
                  aria-label="Elegir operador"
                >
                  {metaLoading ? (
                    <div className="crm-cw-conv-menu__hint">Cargando operadores…</div>
                  ) : meta.agents.length === 0 ? (
                    <div className="crm-cw-conv-menu__hint">
                      {metaLoadError
                        ? 'No se pudieron cargar los operadores.'
                        : 'No hay operadores para asignar.'}
                      {meta.agents_source === 'crm' && !metaLoadError
                        ? ' Configurá el vínculo Chatwoot en Administración → Usuarios.'
                        : null}
                      {metaLoadError ? (
                        <button
                          type="button"
                          className="crm-cw-conv-menu__retry"
                          onClick={() => {
                            metaLoadedRef.current = false;
                            void loadMeta({ force: true });
                          }}
                        >
                          Reintentar
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    meta.agents.map((agent) => {
                      const isAssigned =
                        Number(conversation?.assignee?.id) === Number(agent.id);
                      const displayName = String(agent.name || '').trim() || `Agente #${agent.id}`;
                      return (
                        <button
                          key={agent.id}
                          type="button"
                          role="menuitemradio"
                          aria-checked={isAssigned}
                          className={`crm-cw-agent-pick__row${isAssigned ? ' is-assigned' : ''}`}
                          onClick={() => {
                            if (isAssigned) return;
                            runAction(
                              () =>
                                assignConversation(conversation.id, {
                                  assignee_id: agent.id,
                                  patient_display_name: String(conversationDisplayName || '').trim()
                                }),
                              `Asignado a ${displayName}`,
                              buildActionOverlay(
                                `Asignando a ${displayName}`,
                                `Asignando conversación a ${displayName}`
                              )
                            );
                          }}
                        >
                          <span className="crm-cw-agent-pick__avatar" aria-hidden="true">
                            {getAgentDisplayInitials(displayName)}
                          </span>
                          <span className="crm-cw-agent-pick__body">
                            <span className="crm-cw-agent-pick__name">{displayName}</span>
                            {isAssigned ? (
                              <span className="crm-cw-agent-pick__badge">Asignado</span>
                            ) : null}
                          </span>
                          {isAssigned ? (
                            <ChatwootAgentCheckIcon className="crm-cw-agent-pick__check" />
                          ) : (
                            <span className="crm-cw-agent-pick__idle" aria-hidden="true" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              ) : null}
            </>
          ) : null}

          {SHOW_CONV_MENU_OPEN_IN_TAB ? (
            <>
              <div className="crm-cw-conv-menu__sep" role="separator" />
              <a
                className="crm-cw-conv-menu__item crm-cw-conv-menu__item--link"
                role="menuitem"
                href={buildChatwootConversationUrl(accountId, conversation?.id)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
              >
                <span className="crm-cw-conv-menu__icon" aria-hidden="true">↗</span>
                Abrir en nueva pestaña
              </a>
            </>
          ) : null}
          <button
            type="button"
            className="crm-cw-conv-menu__item"
            role="menuitem"
            onClick={async () => {
              const url = buildChatwootConversationUrl(accountId, conversation?.id);
              try {
                await navigator.clipboard.writeText(url);
                onNotify?.('Enlace copiado', 'ok');
              } catch {
                onNotify?.('No se pudo copiar el enlace', 'error');
              }
              setOpen(false);
            }}
          >
            <span className="crm-cw-conv-menu__icon" aria-hidden="true">🔗</span>
            Copiar enlace de conversación
          </button>

          {showDeleteConversation ? (
            <>
              <div className="crm-cw-conv-menu__sep" role="separator" />

              <button
                type="button"
                className="crm-cw-conv-menu__item crm-cw-conv-menu__item--danger"
                role="menuitem"
                onClick={async () => {
                  if (!conversation?.id || busy || deletingConversation) return;
                  setOpen(false);
                  setSubmenu(null);
                  setMenuPanelStyle(null);
                  const confirm = await fireConfirmDialog({
                    title: 'Eliminar conversación',
                    html: buildCrmSwalBody({
                      lead: 'Se borra la conversación en Chatwoot.',
                      footnote: 'Esta acción no se puede deshacer.'
                    }),
                    icon: 'error',
                    confirmButtonText: 'Eliminar',
                    cancelButtonText: 'Cancelar',
                    confirmButtonColor: '#dc2626',
                    allowOutsideClick: false
                  });
                  if (!confirm.isConfirmed) return;

                  setDeletingConversation(true);
                  onBusyChange?.(true);
                  try {
                    await deleteConversation(conversation.id);
                    onDeleted?.(conversation.id);
                    onNotify?.('Conversación eliminada', 'ok');
                  } catch (err) {
                    onNotify?.(err.message || 'No se pudo eliminar la conversación', 'error');
                  } finally {
                    setDeletingConversation(false);
                    onBusyChange?.(false);
                  }
                }}
              >
                <span className="crm-cw-conv-menu__icon" aria-hidden="true">🗑</span>
                Eliminar conversación
              </button>
            </>
          ) : null}
        </div>,
        document.body
      )
        : null}
    </div>
  );
}
